import React, { useEffect, useState, useCallback } from "react";
import {
    Select,
    message,
    Layout,
    Typography,
    Button,
    Card,
    Table,
    Modal,
    Form,
    Input,
    Empty,
    Spin,
} from "antd";
import { EyeOutlined, SyncOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { useLocation } from "react-router-dom";
import MenuPreviewPanel, { DEFAULT_DESIGN_OPTIONS } from "./MenuPreviewPanel";
import {
  sanitizeDesignOptionsForSave,
  cacheDesignOptions,
} from "../utils/menuDesignOptions";

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const API_BASE = "https://ezwinds.com/api";

const AdminAIMenu = () => {
    // --- Merchant selection (Admin-specific) ---
    const [merchantList, setMerchantList] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const location = useLocation();
    const comebackCustomerId = location.state?.customer_id || null;

    // --- AI Menu state (from AIMenuScreen) ---
    const [sections, setSections] = useState([]);
    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isJsonPreviewOpen, setIsJsonPreviewOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [sectionForm] = Form.useForm();
    const [itemForm] = Form.useForm();
    const [editingItem, setEditingItem] = useState(null);
    const [showEditorMobile, setShowEditorMobile] = useState(false);
    const [designOptions, setDesignOptions] = useState(DEFAULT_DESIGN_OPTIONS);

    // Use selected merchant's customer_id (admin) instead of localStorage (logged-in user)
    const customerId = selectedMerchant?.customer_id || null;

    // --- Fetch merchants ---
    const fetchMerchants = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/fetchMerchantForMode`);
            const list = data?.data || [];
            setMerchantList(list);
            if (comebackCustomerId) {
                const merchant = list.find((m) => m.customer_id === comebackCustomerId);
                if (merchant) setSelectedMerchant(merchant);
            }
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch merchants");
        }
    };

    const handleMerchantChange = (customer_id) => {
        const merchant = merchantList.find((m) => m.customer_id === customer_id);
        setSelectedMerchant(merchant);
        if (merchant) {
            console.log("Selected Merchant:", merchant);
        }
        // Reset menu state when switching merchant
        setSections([]);
        setSelectedSectionId(null);
        setDesignOptions(DEFAULT_DESIGN_OPTIONS);
        setIsDataLoaded(false);
    };

    useEffect(() => {
        fetchMerchants();
    }, []);

    // --- API: FETCH MENU (triggered when merchant selected) ---
    const fetchCustomerMenu = useCallback(async (customerId) => {
        if (!customerId) {
            console.error("Fetch aborted: No customerId provided.");
            setIsDataLoaded(true);
            return;
        }

        console.log(`Fetching menu for customerId: ${customerId}...`);
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_BASE}/getCustomerMenu`, {
                customerId: customerId,
            });
            
        

            const menuData = response.data?.storeDetails;
            const menuWrapper = menuData?.menu || menuData;
            if (menuWrapper && menuWrapper.sections) {
                const fetchedSections = menuWrapper.sections;
                setSections(fetchedSections);
                if (fetchedSections.length > 0) {
                    setSelectedSectionId(fetchedSections[0].customerId);
                }
                if (menuData?.designOptions) {
                    setDesignOptions({ ...DEFAULT_DESIGN_OPTIONS, ...menuData.designOptions });
                }
                message.success("Menu loaded from cloud.");
            } else {
                setSections([]);
                message.info("No menu found for this merchant yet.");
            }
        } catch (err) {
            console.error("Fetch error:", err);
            message.warning("Using local backup (Server unreachable).");
            const raw = localStorage.getItem(`ai_menu_${customerId}`);
            if (raw) {
                try {
                    setSections(JSON.parse(raw));
                } catch (_) {}
            }
        } finally {
            setIsLoading(false);
            setIsDataLoaded(true);
        }
    }, []);

    // Fetch menu when merchant is selected
    useEffect(() => {
        if (selectedMerchant?.customer_id) {
            fetchCustomerMenu(selectedMerchant.customer_id);
        } else {
            setSections([]);
            setSelectedSectionId(null);
            setIsDataLoaded(true);
        }
    }, [selectedMerchant?.customer_id, fetchCustomerMenu]);

    // --- API: UPDATE (SYNC) ---
    const syncMenuToServer = async (updatedSections, designOpts = designOptions) => {
        if (!customerId || !isDataLoaded) return;

        setIsSyncing(true);
        const totalItems = updatedSections.reduce((sum, s) => sum + (s.items?.length || 0), 0);

        // Exclude large base64; keep small thumb (~50KB) for preview. Image URL is on S3.
        const safeDesignOpts = sanitizeDesignOptionsForSave(designOpts);
        console.log("[AdminAIMenu] syncMenuToServer", {
          thumbIncluded: !!safeDesignOpts.backgroundImageThumb,
          thumbLen: safeDesignOpts.backgroundImageThumb?.length,
          backgroundUrl: safeDesignOpts.backgroundImageUrl,
        });
        const payload = {
            customerId,
            menu: {
                menu: {
                    sections: updatedSections.map((s) => ({
                        id: s.id,
                        name: s.name,
                        items: s.items || [],
                    })),
                },
                metadata: {
                    generatedAt: new Date().toISOString(),
                    totalSections: updatedSections.length,
                    totalItems,
                },
                designOptions: safeDesignOpts,
            },
        };

        try {
            await axios.post(`${API_BASE}/updateCustomerMenu`, payload);
            localStorage.setItem(`ai_menu_${customerId}`, JSON.stringify(updatedSections));
            cacheDesignOptions(customerId, designOpts);
            message.success("Menu saved successfully.");
        } catch (err) {
            console.error("Sync Error:", err);
            message.error("Sync failed.");
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSave = async () => {
        if (!customerId || !isDataLoaded) {
            message.warning("Please select a merchant and wait for menu to load.");
            return;
        }
        await syncMenuToServer(sections, designOptions);
    };

    // --- HELPERS ---
    const formatPrice = (value) => {
        if (!value) return "—";
        const str = value.toString().trim();
        const num = Number(str);
        return !isNaN(num) && str !== "" ? `$${num.toFixed(2)}` : str;
    };

    // --- CRUD HANDLERS ---
    const handleSectionSubmit = () => {
        sectionForm.validateFields().then((v) => {
            const newSec = { id: Date.now().toString(), name: v.name.trim(), items: [] };
            const updated = [...sections, newSec];
            setSections(updated);
            if (!selectedSectionId) setSelectedSectionId(newSec.id);
            setIsSectionModalOpen(false);
            syncMenuToServer(updated);
        });
    };

    const handleDeleteSection = (id) => {
        const updated = sections.filter((s) => s.id !== id);
        setSections(updated);
        if (selectedSectionId === id) setSelectedSectionId(updated[0]?.id || null);
        syncMenuToServer(updated);
    };

    const handleItemSubmit = () => {
        itemForm.validateFields().then((v) => {
            const newItem = {
                id: editingItem?.id || Date.now().toString(),
                name: v.name.trim(),
                ...(v.price != null &&
                    String(v.price).trim() !== "" && {
                        price: String(v.price).trim(),
                    }),
            };
            const updated = sections.map((s) =>
                s.id === selectedSectionId
                    ? {
                          ...s,
                          items: editingItem
                              ? s.items.map((i) => (i.id === editingItem.id ? newItem : i))
                              : [...(s.items || []), newItem],
                      }
                    : s
            );
            setSections(updated);
            setIsItemModalOpen(false);
            syncMenuToServer(updated);
        });
    };

    const handleDeleteItem = (itemId) => {
        const updated = sections.map((s) =>
            s.id === selectedSectionId ? { ...s, items: s.items.filter((i) => i.id !== itemId) } : s
        );
        setSections(updated);
        syncMenuToServer(updated);
    };

    const confirmDeleteItem = (record) => {
        const itemName = record?.name?.trim() || "this item";
        Modal.confirm({
            title: "Delete item",
            content: (
                <span>
                    Are you sure you want to delete <strong>{itemName}</strong>?
                </span>
            ),
            okText: "OK",
            cancelText: "Cancel",
            okButtonProps: { danger: true },
            onOk: () => handleDeleteItem(record.id),
        });
    };

    const currentSection = sections.find((s) => s.id === selectedSectionId) || null;

    const columns = [
        { title: "Item", dataIndex: "name", key: "name" },
        { title: "Price", dataIndex: "price", key: "price", render: formatPrice },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, record) => (
                <div style={{ display: "flex", gap: 8 }}>
                    <Button
                        type="link"
                        onClick={() => {
                            setEditingItem(record);
                            itemForm.setFieldsValue(record);
                            setIsItemModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button type="link" danger onClick={() => confirmDeleteItem(record)}>
                        Delete
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div className="modSpace">
            <h2>AI Menu Creation</h2>

            {/* Merchant selection section */}
            <div className="relContnet">
                <Select
                    showSearch
                    virtual={false}
                    style={{ width: 300, marginBottom: 20 }}
                    placeholder="Select Merchant"
                    value={selectedMerchant?.customer_id || null}
                    onChange={handleMerchantChange}
                    filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={Array.from(
                        new Map(
                            merchantList.map((m) => [
                                m.customer_id,
                                { label: m.full_name, value: m.customer_id },
                            ])
                        ).values()
                    )}
                />

                {selectedMerchant && (
                    <div className="boxDivs">
                        <h3>Selected Merchant</h3>
                        <p>
                            <b>Name:</b> {selectedMerchant.name}
                        </p>
                        <p>
                            <b>Mobile:</b> {selectedMerchant.mobile || "N/A"}
                        </p>
                        <p>
                            <b>Customer ID:</b> {selectedMerchant.customer_id}
                        </p>
                    </div>
                )}
            </div>

            {/* AI Menu content - shown only when merchant is selected */}
            {selectedMerchant && (
                <Layout style={{ minHeight: "60vh", background: "#f5f5f5", marginTop: 24 }}>
                    <Header className="menu-header-mobile headerAI">
                        <div className="headerAi_menu">
                            <Title level={4} style={{ margin: 0 }}>
                                Menu Editor
                            </Title>
                            <Text type="secondary">
                                Design a simple restaurant menu for {selectedMerchant.name || "merchant"}.
                            </Text>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Button
                                icon={<SaveOutlined spin={isSyncing} />}
                                onClick={handleSave}
                                disabled={isSyncing || !customerId}
                            >
                                Save
                            </Button>
                            <Button
                                icon={<SyncOutlined spin={isLoading} />}
                                onClick={() => fetchCustomerMenu(customerId)}
                                disabled={isLoading}
                            >
                                Refresh
                            </Button>
                            <Button
                                icon={<EyeOutlined />}
                                onClick={() => setIsJsonPreviewOpen(true)}
                                disabled={!sections.length}
                            >
                                Preview JSON
                            </Button>
                        </div>
                    </Header>

                    <Spin spinning={isLoading} tip="Loading Menu from Cloud...">
                        <Layout className="menu-layout-mobile aiMenus_wraps">
                            <Sider className="menu-sider-mobile Ai_sidebar">
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                                    <Text strong>Sections</Text>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => {
                                            sectionForm.resetFields();
                                            setIsSectionModalOpen(true);
                                        }}
                                    >
                                        + Section
                                    </Button>
                                </div>
                                <div className="sectionList">
                                    {sections.length === 0 ? (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            {sections.map((s) => (
                                                <Card
                                                    key={s.id}
                                                    size="small"
                                                    hoverable
                                                    style={{
                                                        borderColor: s.id === selectedSectionId ? "#1677ff" : "#f0f0f0",
                                                        background: s.id === selectedSectionId ? "#e6f4ff" : "#fff",
                                                    }}
                                                    onClick={() => {
                                                        setSelectedSectionId(s.id);
                                                        setShowEditorMobile(true);
                                                    }}
                                                >
                                                    <div className="alignBoxs-bx">
                                                        <Text strong ellipsis>
                                                            {s.name}
                                                        </Text>
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            danger
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteSection(s.id);
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Sider>

                            <Content style={{ margin: "20px" }}>
                                <div className="gridsOuter_preview">
                                    <div className={`shadowBoxs editor-panel ${showEditorMobile ? "editor-open" : ""}`}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                                            <div>
                                                <Button
                                                    type="link"
                                                    className="mobile-back-btn"
                                                    onClick={() => setShowEditorMobile(false)}
                                                >
                                                    <i className="bi bi-arrow-left"></i> Back
                                                </Button>
                                                <Title level={4} style={{ marginBottom: 0 }}>
                                                    {currentSection?.name || "Select Section"}
                                                </Title>
                                            </div>
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    itemForm.resetFields();
                                                    setEditingItem(null);
                                                    setIsItemModalOpen(true);
                                                }}
                                                disabled={!currentSection}
                                            >
                                                + Add Item
                                            </Button>
                                        </div>
                                        <Table
                                            rowKey="id"
                                            dataSource={currentSection?.items || []}
                                            columns={columns}
                                            bordered
                                            pagination={false}
                                        />
                                    </div>

                                    <MenuPreviewPanel
                                        sections={sections}
                                        customerId={customerId}
                                        formatPrice={formatPrice}
                                        setSections={setSections}
                                        selectedSectionId={selectedSectionId}
                                        designOptions={designOptions}
                                        onDesignOptionsChange={setDesignOptions}
                                        onPersistMenu={(opts) =>
                                          syncMenuToServer(sections, opts)
                                        }
                                    />
                                </div>
                            </Content>
                        </Layout>
                    </Spin>
                </Layout>
            )}

            <Modal
                title="Create Section"
                open={isSectionModalOpen}
                onOk={handleSectionSubmit}
                onCancel={() => setIsSectionModalOpen(false)}
            >
                <Form form={sectionForm} layout="vertical">
                    <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingItem ? "Edit Item" : "Add Item"}
                open={isItemModalOpen}
                onOk={handleItemSubmit}
                onCancel={() => setIsItemModalOpen(false)}
            >
                <Form form={itemForm} layout="vertical">
                    <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Price" name="price">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="JSON Preview"
                open={isJsonPreviewOpen}
                onCancel={() => setIsJsonPreviewOpen(false)}
                width={800}
                footer={<Button onClick={() => setIsJsonPreviewOpen(false)}>Close</Button>}
            >
                <pre
                    style={{
                        background: "#f5f5f5",
                        padding: 16,
                        overflow: "auto",
                        borderRadius: 8,
                    }}
                >
                    {JSON.stringify(
                        {
                            menu: { menu: { sections } },
                            metadata: {
                                generatedAt: new Date().toISOString(),
                                totalSections: sections.length,
                            },
                        },
                        null,
                        2
                    )}
                </pre>
            </Modal>
        </div>
    );
};

export default AdminAIMenu;
